import { type CompanyDto, type MeDto, type RoleDto, STAFF_FUNCTIONS, type StaffFunction, type UserDto, type UserFunctionDto, type WarehouseDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, MultiSelect, SegmentedControl, Stack, Table, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { api } from '../../api';
import { CompaniesField } from '../../components/CompaniesField';
import { QueryState } from '../../components/QueryState';
import { EmptyState, SectionCard } from '../../components/ui';
import { UserPermissionsPanel } from '../../components/UserPermissionsPanel';
import { useTrades } from '../../data';
import { notifyError, notifyOk } from '../../notify';
import { userBody } from './userBody';

/** Branże: „nie dostaje” / wszystkie / wybrane. */
type TradeChoice = { mode: 'off' | 'all' | 'some'; trades: string[] };

const toChoice = (fns: UserFunctionDto[], fn: string): TradeChoice => {
  const mine = fns.filter((f) => f.function === fn);
  if (!mine.length) return { mode: 'off', trades: [] };
  if (mine.some((f) => f.tradeCode === null)) return { mode: 'all', trades: [] };
  return { mode: 'some', trades: mine.map((f) => f.tradeCode!).sort() };
};
const fromChoice = (fn: StaffFunction, c: TradeChoice): { function: StaffFunction; tradeCode: string | null }[] =>
  c.mode === 'off' ? [] : c.mode === 'all' ? [{ function: fn, tradeCode: null }] : c.trades.map((t) => ({ function: fn, tradeCode: t }));
const same = (a: TradeChoice, b: TradeChoice) => a.mode === b.mode && (a.mode !== 'some' || [...a.trades].sort().join() === [...b.trades].sort().join());

/** Wybór branż: tryb + lista (przy „wybrane”). */
function TradesPicker({ value, onChange, offLabel, disabled }: { value: TradeChoice; onChange: (c: TradeChoice) => void; offLabel: string; disabled?: boolean }) {
  const trades = useTrades();
  const data = (trades.data ?? []).filter((t) => t.active).map((t) => ({ value: t.code, label: t.name }));
  return (
    <Stack gap={6}>
      <SegmentedControl
        size="xs"
        disabled={disabled}
        value={value.mode}
        onChange={(m) => onChange({ ...value, mode: m as TradeChoice['mode'] })}
        data={[
          { value: 'off', label: offLabel },
          { value: 'all', label: 'Wszystkie branże' },
          { value: 'some', label: 'Wybrane branże' },
        ]}
      />
      {value.mode === 'some' && (
        <MultiSelect
          size="xs"
          disabled={disabled}
          placeholder={value.trades.length ? undefined : 'Wybierz branże'}
          data={[...data, ...value.trades.filter((c) => !data.some((d) => d.value === c)).map((c) => ({ value: c, label: c }))]}
          value={value.trades}
          onChange={(trades) => onChange({ mode: 'some', trades })}
          error={value.trades.length === 0}
          comboboxProps={{ withinPortal: true }}
        />
      )}
    </Stack>
  );
}

function SaveBar({ dirty, loading, disabled, onReset, onSave, label = 'Zapisz' }: { dirty: boolean; loading: boolean; disabled?: boolean; onReset: () => void; onSave: () => void; label?: string }) {
  return (
    <Group justify="flex-end" gap="xs">
      {dirty && (
        <Text size="xs" c="var(--app-text-orange)" fw={500} mr="auto">
          Niezapisane zmiany
        </Text>
      )}
      <Button size="xs" variant="default" disabled={!dirty} onClick={onReset}>
        Przywróć
      </Button>
      <Button size="xs" disabled={!dirty || disabled} loading={loading} onClick={onSave}>
        {label}
      </Button>
    </Group>
  );
}

function useInvalidateAccess(userId: string) {
  const qc = useQueryClient();
  return () => {
    for (const key of [['users'], ['user-functions', userId], ['user-permissions', userId], ['trade-functions'], ['process-inbox'], ['me'], ['users-lite'], ['roles']]) void qc.invalidateQueries({ queryKey: key });
  };
}

/** Role pracownika i kolejki zadań, które za nimi idą — z branżami (docs/06 §8). */
function RolesCard({ user, roles, functions, editable, onDirty }: { user: UserDto; roles: RoleDto[]; functions: UserFunctionDto[]; editable: boolean; onDirty: (d: boolean) => void }) {
  const invalidate = useInvalidateAccess(user.id);
  const [codes, setCodes] = useState<string[]>(user.roleCodes);
  const [choices, setChoices] = useState<Record<string, TradeChoice>>({});
  useEffect(() => {
    setCodes(user.roleCodes);
    setChoices({});
  }, [user, functions]);
  // kolejki z wybranych ról: kolejka → role, które ją niosą
  const queues = useMemo(() => {
    const m = new Map<StaffFunction, string[]>();
    for (const r of roles.filter((x) => codes.includes(x.code))) for (const q of r.queues) m.set(q as StaffFunction, [...(m.get(q as StaffFunction) ?? []), r.name]);
    return [...m.entries()];
  }, [roles, codes]);
  // nowa kolejka (rola dodana w tym formularzu) — domyślnie wszystkie branże, jak po zapisie na serwerze
  const choiceOf = (fn: StaffFunction): TradeChoice =>
    choices[fn] ?? (functions.some((f) => f.function === fn) || roles.some((r) => user.roleCodes.includes(r.code) && r.queues.includes(fn)) ? toChoice(functions, fn) : { mode: 'all', trades: [] });
  const rolesDirty = [...codes].sort().join() !== [...user.roleCodes].sort().join();
  const queuesDirty = queues.some(([fn]) => !same(choiceOf(fn), toChoice(functions, fn)));
  const dirty = rolesDirty || queuesDirty;
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const invalid = queues.some(([fn]) => choiceOf(fn).mode === 'some' && choiceOf(fn).trades.length === 0);

  const save = useMutation({
    mutationFn: async () => {
      if (rolesDirty) await api(`/users/${user.id}`, { method: 'PUT', body: userBody(user, { roleCodes: codes }) });
      const monter = functions.filter((f) => f.function === 'monter').map((f) => ({ function: f.function, tradeCode: f.tradeCode }));
      await api(`/users/${user.id}/functions`, { method: 'PUT', body: { functions: [...queues.flatMap(([fn]) => fromChoice(fn, choiceOf(fn))), ...monter] } });
    },
    onSuccess: () => {
      notifyOk('Zapisano role i zadania — obowiązują od razu');
      invalidate();
    },
    onError: notifyError,
  });

  if (!user.hasAccount)
    return (
      <SectionCard title="Role i zadania w sprawach">
        <Alert color="gray" variant="light">
          Pracownik bez konta nie ma ról ani zadań w sprawach. Załóż konto w zakładce „Konto i logowanie” — wtedy wybierzesz role.
        </Alert>
      </SectionCard>
    );

  return (
    <SectionCard title="Role i zadania w sprawach">
      <Stack gap="sm">
        <MultiSelect
          label="Role"
          description="Rola daje uprawnienia i moduły menu; niektóre role niosą też kolejkę zadań w sprawach (Ustawienia → Role)."
          data={roles.map((r) => ({ value: r.code, label: r.name }))}
          value={codes}
          onChange={setCodes}
          disabled={!editable}
          error={codes.length === 0 ? 'Konto wymaga co najmniej jednej roli' : undefined}
        />
        {queues.length === 0 ? (
          <Text size="sm" c="dimmed">
            Role pracownika nie mają kolejek zadań — nie dostaje zadań w sprawach (ofert, umów, planowania, rozliczeń).
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table verticalSpacing={8}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={190}>Kolejka zadań</Table.Th>
                  <Table.Th>W jakich branżach dostaje zadania</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {queues.map(([fn, roleNames]) => (
                  <Table.Tr key={fn}>
                    <Table.Td>
                      <Text size="sm" fw={500}>
                        {STAFF_FUNCTIONS[fn]}
                      </Text>
                      <Text size="xs" c="dimmed">
                        z roli: {roleNames.join(', ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <TradesPicker value={choiceOf(fn)} onChange={(c) => setChoices((x) => ({ ...x, [fn]: c }))} offLabel="Nie dostaje" disabled={!editable} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        <Text size="xs" c="dimmed">
          „Wszystkie branże” obejmuje też branże dodane później. Zmiana nie zabiera zadań, które pracownik już przejął.
        </Text>
        {editable && (
          <SaveBar
            dirty={dirty}
            loading={save.isPending}
            disabled={invalid || codes.length === 0}
            onReset={() => {
              setCodes(user.roleCodes);
              setChoices({});
            }}
            onSave={() => save.mutate()}
          />
        )}
      </Stack>
    </SectionCard>
  );
}

/** Firmy pracownika — zawężają dane firmowe (oferty poniżej progu, umowy, finanse, faktury, zakupy, magazyn, flota, zestawienia). */
function CompaniesCard({ user, companies, editable, onDirty }: { user: UserDto; companies: CompanyDto[]; editable: boolean; onDirty: (d: boolean) => void }) {
  const invalidate = useInvalidateAccess(user.id);
  const [all, setAll] = useState(user.allCompanies);
  const [ids, setIds] = useState(user.companyIds);
  useEffect(() => {
    setAll(user.allCompanies);
    setIds(user.companyIds);
  }, [user]);
  const dirty = all !== user.allCompanies || (!all && [...ids].sort().join() !== [...user.companyIds].sort().join());
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api(`/users/${user.id}`, { method: 'PUT', body: userBody(user, { allCompanies: all, companyIds: all ? user.companyIds : ids }) }),
    onSuccess: () => {
      notifyOk('Zapisano firmy pracownika — obowiązują od razu');
      invalidate();
    },
    onError: notifyError,
  });
  return (
    <SectionCard title="Firmy">
      <Stack gap="sm">
        <CompaniesField companies={companies} all={all} ids={ids} onChange={(a, i) => (setAll(a), setIds(i))} />
        <Text size="xs" c="dimmed">
          Firmy zawężają <b>dane firmowe</b>: umowy, finanse i faktury, zakupy, magazyn, flotę, zestawienia i zatwierdzanie rabatów. Klientów, spraw, ofert i katalogu nie zawężają.
        </Text>
        {editable && <SaveBar dirty={dirty} loading={save.isPending} disabled={!all && ids.length === 0} onReset={() => (setAll(user.allCompanies), setIds(user.companyIds))} onSave={() => save.mutate()} />}
      </Stack>
    </SectionCard>
  );
}

/** Magazyny pracownika (docs/14 §2.3): puste = wszystkie magazyny jego firm; zawężenie działa na serwerze. */
function WarehousesCard({ userId, editable, onDirty }: { userId: string; editable: boolean; onDirty: (d: boolean) => void }) {
  const whs = useQuery({ queryKey: ['warehouses', 'all'], queryFn: () => api<WarehouseDto[]>('/warehouses') });
  const own = useQuery({ queryKey: ['user-warehouses', userId], queryFn: () => api<string[]>(`/users/${userId}/warehouses`) });
  const [sel, setSel] = useState<string[] | null>(null);
  const qc = useQueryClient();
  const value = sel ?? own.data ?? [];
  useEffect(() => onDirty(sel !== null), [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api<string[]>(`/users/${userId}/warehouses`, { method: 'PUT', body: { warehouseIds: value } }),
    onSuccess: (r) => {
      notifyOk('Zapisano magazyny pracownika');
      qc.setQueryData(['user-warehouses', userId], r);
      setSel(null);
    },
    onError: notifyError,
  });
  if (!whs.data?.length) return null;
  return (
    <SectionCard title="Magazyny">
      <Stack gap="sm">
        <MultiSelect
          label="Dostęp do magazynów"
          description="Puste — wszystkie magazyny firm pracownika. Działa przy uprawnieniach magazynowych z ról."
          placeholder={value.length ? undefined : 'wszystkie magazyny firm pracownika'}
          data={whs.data.map((w) => ({ value: w.id, label: `${w.companyCode} · ${w.code} — ${w.name}` }))}
          value={value}
          onChange={setSel}
          clearable
          disabled={!editable}
        />
        {editable && <SaveBar dirty={sel !== null} loading={save.isPending} onReset={() => setSel(null)} onSave={() => save.mutate()} />}
      </Stack>
    </SectionCard>
  );
}

/** Branże pracy w terenie (funkcja „monter”) — planowanie osób spoza ekip; nie daje uprawnień. */
function WorkTradesCard({ user, functions, editable, onDirty }: { user: UserDto; functions: UserFunctionDto[]; editable: boolean; onDirty: (d: boolean) => void }) {
  const invalidate = useInvalidateAccess(user.id);
  const initial = toChoice(functions, 'monter');
  const [c, setC] = useState(initial);
  useEffect(() => setC(toChoice(functions, 'monter')), [functions]);
  const dirty = !same(c, initial);
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () =>
      api(`/users/${user.id}/functions`, {
        method: 'PUT',
        body: { functions: [...functions.filter((f) => f.function !== 'monter').map((f) => ({ function: f.function, tradeCode: f.tradeCode })), ...fromChoice('monter', c)] },
      }),
    onSuccess: () => {
      notifyOk('Zapisano branże pracy');
      invalidate();
    },
    onError: notifyError,
  });
  return (
    <SectionCard title="Praca w terenie — branże">
      <Stack gap="sm">
        <TradesPicker value={c} onChange={setC} offLabel="Nie pracuje w terenie" disabled={!editable} />
        <Text size="xs" c="dimmed">
          W czym pracownik wykonuje prace (planowanie osób spoza ekip, filtr branż). Uprawnień nie daje — do pracy w aplikacji mobilnej potrzebna jest rola, np. Instalator.
        </Text>
        {editable && <SaveBar dirty={dirty} loading={save.isPending} disabled={c.mode === 'some' && c.trades.length === 0} onReset={() => setC(initial)} onSave={() => save.mutate()} />}
      </Stack>
    </SectionCard>
  );
}

/**
 * Karta pracownika → „Dostęp”: wszystko, co decyduje o tym, co pracownik widzi i robi (docs/06 §8) —
 * role z kolejkami zadań, firmy, magazyny, branże pracy, wyjątki i wynik „co może i dlaczego”.
 */
export function AccessTab({
  user,
  roles,
  companies,
  me,
  readOnly,
  onDirty,
  maw,
}: {
  user: UserDto;
  roles: RoleDto[];
  companies: CompanyDto[];
  me: MeDto;
  readOnly: boolean;
  onDirty: (d: boolean) => void;
  maw: number;
}) {
  const fns = useQuery({ queryKey: ['user-functions', user.id], queryFn: () => api<UserFunctionDto[]>(`/users/${user.id}/functions`) });
  // każda karta zgłasza swoje niezapisane zmiany; zakładka ma zmiany, gdy którakolwiek je ma
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const report = (k: string) => (d: boolean) => setDirty((x) => (x[k] === d ? x : { ...x, [k]: d }));
  const any = Object.values(dirty).some(Boolean);
  useEffect(() => onDirty(any), [any]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirty(false), []); // eslint-disable-line react-hooks/exhaustive-deps
  const editable = !readOnly;
  const box = (children: ReactNode) => <Stack maw={maw}>{children}</Stack>;

  return (
    <QueryState isLoading={fns.isLoading} error={fns.error}>
      {fns.data &&
        box(
          <>
            <Text size="sm" c="dimmed">
              Co pracownik może w CRM: <b>role</b> (uprawnienia i kolejki zadań) zawężone do jego <b>firm</b>, plus <b>wyjątki</b> nadane albo odebrane tylko jemu.
            </Text>
            <RolesCard user={user} roles={roles} functions={fns.data} editable={editable} onDirty={report('roles')} />
            <Group align="stretch" grow wrap="wrap" gap="md">
              <CompaniesCard user={user} companies={companies} editable={editable} onDirty={report('companies')} />
              {me.features.magazyn && user.hasAccount ? <WarehousesCard userId={user.id} editable={editable} onDirty={report('warehouses')} /> : null}
            </Group>
            <WorkTradesCard user={user} functions={fns.data} editable={editable} onDirty={report('trades')} />
            {user.hasAccount ? (
              <UserPermissionsPanel user={user} editable={editable} onDirty={report('exceptions')} />
            ) : (
              <SectionCard title="Uprawnienia">
                <EmptyState compact title="Pracownik bez konta" hint="Nie loguje się do CRM ani do aplikacji — nie ma uprawnień. Może być w ekipach i w planie prac." />
              </SectionCard>
            )}
            {!user.active && (
              <Badge color="gray" variant="light">
                Pracownik nieaktywny — nie loguje się i nie dostaje zadań
              </Badge>
            )}
          </>,
        )}
    </QueryState>
  );
}
